import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../i18n/LanguageContext';
import { Search, MapPin, Calendar, ArrowRight, Flame, Star, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { generateMemorialUrl } from './MemorialPage';
import MemorialPlaceholder from '../components/MemorialPlaceholder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ObituaryCard({ item, index, lang }) {
  const { t } = useTranslation();
  
  // Map old values to new values (for backward compatibility during migration)
  const typeMapping = {
    'amtlich': 'publiziert',
    'prominent': 'persoenlichkeit',
    'premium': 'individuell'
  };
  const normalizedType = typeMapping[item.eintragsart] || item.eintragsart;
  
  const isIndividuell = normalizedType === 'individuell';
  const isPersoenlichkeit = normalizedType === 'persoenlichkeit';
  const isLetzterGruss = normalizedType === 'letzter_gruss';
  const memorialUrl = generateMemorialUrl(item, lang);

  const getBadgeClass = () => {
    if (isIndividuell) return 'badge-individuell';
    if (isPersoenlichkeit) return 'badge-persoenlichkeit';
    if (isLetzterGruss) return 'badge-letzter-gruss';
    return 'badge-publiziert';
  };

  return (
    <Link
      to={memorialUrl}
      className={`memorial-card ${isIndividuell ? 'premium' : ''} stagger-item block bg-white rounded-sm overflow-hidden`}
      data-testid={`obituary-card-${item.id}`}
    >
      {/* Mobile: Ultra-kompakte Zeile */}
      <div className="sm:hidden px-3 py-2.5 border-b border-[#F1F5F9] flex items-center gap-3">
        {/* Initialen-Avatar */}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#F8FAFC] to-[#E2E8F0] flex items-center justify-center flex-shrink-0 border border-[#E2E8F0]">
          <span className="text-xs font-medium text-[#64748B]">
            {item.vorname?.[0]}{item.nachname?.[0]}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-accent text-[16px] text-[#1E293B] leading-tight truncate">
            {item.vorname} {item.nachname}
          </h3>
          {item.sterbedatum && (
            <p className="text-[12px] text-[#94A3B8] mt-0.5">
              † {new Date(item.sterbedatum).toLocaleDateString('de-CH', { day: 'numeric', month: 'numeric', year: 'numeric' })}
            </p>
          )}
          <div className="flex items-center gap-1.5 text-[12px] text-[#94A3B8] mt-0.5">
            {!normalizedType || normalizedType === 'publiziert' ? null : (
              <span className={`${getBadgeClass()} !text-[10px] !px-1 !py-0`}>
                {t(`types.${normalizedType}`)}
              </span>
            )}
            {item.wohnort && <span>{item.wohnort}</span>}
            {item.alter_jahre && <span>· {item.alter_jahre} J.</span>}
          </div>
        </div>
        
        {/* Kerzen oder Pfeil */}
        <div className="flex-shrink-0">
          {item.candle_count > 0 ? (
            <span className="flex items-center gap-0.5 text-[11px] text-[#D97706] font-medium">
              <Flame className="w-3.5 h-3.5 text-[#F59E0B]" />
              {item.candle_count}
            </span>
          ) : (
            <svg className="w-4 h-4 text-[#CBD5E1]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
            </svg>
          )}
        </div>
      </div>

      {/* Desktop: Volle Karte mit Bild */}
      <div className="hidden sm:block">
        {/* Image */}
        {item.bild_url ? (
          <div className="aspect-[4/3] overflow-hidden bg-[#F1F5F9]">
            <img src={item.bild_url} alt={`${item.vorname} ${item.nachname}`}
              loading="lazy"
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" />
          </div>
        ) : (
          <div className="aspect-[4/3] overflow-hidden">
            <MemorialPlaceholder 
              vorname={item.vorname}
              nachname={item.nachname}
              kantonCode={item.kanton_code}
              sternzeichen={item.sternzeichen_name}
              size="full"
              className=""
            />
          </div>
        )}

        <div className="p-5">
          {/* Badge */}
          <div className="mb-3">
            <span className={getBadgeClass()}>
              {t(`types.${normalizedType}`)}
            </span>
          </div>

          {/* Name */}
          <h3 className="font-serif-accent text-lg text-[#1E293B] leading-tight">
            {item.vorname} {item.nachname}
          </h3>
          {item.ledigname && (
            <p className="text-xs text-[#94A3B8] mt-0.5">geb. {item.ledigname}</p>
          )}

          {/* Dates */}
          <div className="flex items-center gap-1.5 mt-3 text-xs text-[#64748B]">
            <Calendar className="w-3 h-3" />
            <span>
              {item.geburtsdatum && new Date(item.geburtsdatum).toLocaleDateString('de-CH')}
              {item.geburtsdatum && item.sterbedatum && ' – '}
              {item.sterbedatum && new Date(item.sterbedatum).toLocaleDateString('de-CH')}
            </span>
          </div>

          {/* Location */}
          {item.wohnort && (
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-[#64748B]">
              <MapPin className="w-3 h-3" />
              <span>{item.wohnort}{item.kanton_code ? ` (${item.kanton_code.toUpperCase()})` : ''}</span>
            </div>
          )}

          {/* Candle count */}
          {(item.candle_count > 0 || item.condolence_count > 0) && (
            <div className="flex items-center gap-3 mt-2 text-xs text-[#94A3B8]">
              {item.candle_count > 0 && (
                <span className="flex items-center gap-1">
                  <Flame className="w-3 h-3 text-[#C5A059]" />
                  {item.candle_count}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}


export default function HomePage() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [vipWithImages, setVipWithImages] = useState([]);
  const [recentCondolences, setRecentCondolences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/inserate/recent?limit=8`).then(r => r.json()),
      fetch(`${API}/inserate/featured`).then(r => r.json()),
      fetch(`${API}/inserate/vip`).then(r => r.json()),
      fetch(`${API}/inserate/stats`).then(r => r.json()).catch(() => null),
      fetch(`${API}/inserate/recent-condolences?limit=20`).then(r => r.json()).catch(() => []),
    ]).then(([recentData, featuredData, vipData, statsData, condolencesData]) => {
      setRecent(recentData);
      setFeatured(featuredData);
      const vipsWithImg = (vipData.items || vipData || []).filter(v => v.bild_url);
      setVipWithImages(vipsWithImg);
      setStats(statsData);
      setRecentCondolences(Array.isArray(condolencesData) ? condolencesData : []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/suche?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="page-enter" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{ backgroundImage: `url(https://images.unsplash.com/photo-1618688259579-54fc3b432a3b?crop=entropy&cs=srgb&fm=webp&w=1440&q=70&ixlib=rb-4.1.0)` }}
        />
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight">
            {t('hero.title')}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#CBD5E1] max-w-lg mx-auto leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <form onSubmit={handleSearch} className="mt-8 flex items-center max-w-xl mx-auto" data-testid="hero-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('hero.search_placeholder')}
                className="pl-11 pr-4 py-3 bg-white/95 border-0 rounded-sm text-sm text-[#1E293B] placeholder:text-[#94A3B8] focus:ring-1 focus:ring-[#C5A059]"
                data-testid="hero-search-input"
              />
            </div>
            <Button
              type="submit"
              className="ml-2 bg-[#C5A059] hover:bg-[#B08D45] text-white px-6 py-3 rounded-sm text-sm font-medium"
              data-testid="hero-search-button"
            >
              {t('hero.search_button')}
            </Button>
          </form>

          {/* Stats */}
          {stats && (
            <div className="mt-8 flex items-center justify-center gap-8" data-testid="hero-stats">
              <div className="text-center">
                <p className="font-heading text-3xl md:text-4xl font-medium text-white">
                  {stats.total.toLocaleString('de-CH')}
                </p>
                <p className="text-xs text-[#CBD5E1] mt-1 uppercase tracking-wider">{t('home.total_inserate')}</p>
              </div>
              <div className="w-px h-10 bg-white/20" />
              <div className="text-center">
                <p className="font-heading text-3xl md:text-4xl font-medium text-[#C5A059]">
                  {stats.this_month.toLocaleString('de-CH')}
                </p>
                <p className="text-xs text-[#CBD5E1] mt-1 uppercase tracking-wider">{t('home.this_month')}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* VIP Personalities Banner */}
      {vipWithImages.length > 0 && (
        <Link 
          to="/persoenlichkeiten" 
          className="block bg-gradient-to-r from-[#1E293B] to-[#334155] hover:from-[#334155] hover:to-[#475569] transition-all duration-300 group"
          data-testid="vip-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              {/* Label */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Star className="w-4 h-4 text-[#C5A059]" />
                <span className="text-xs font-medium text-[#C5A059] uppercase tracking-wider hidden sm:inline">
                  {t('home.personalities')}
                </span>
              </div>
              
              {/* Images Strip */}
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-1 sm:gap-2">
                  {vipWithImages.slice(0, 12).map((vip, index) => (
                    <div 
                      key={vip.id}
                      className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-[#475569] group-hover:border-[#C5A059] transition-colors"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <img
                        src={vip.bild_url}
                        alt={`${vip.vorname} ${vip.nachname}`}
                        loading="lazy"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
                      />
                    </div>
                  ))}
                  {vipWithImages.length > 12 && (
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#475569] border-2 border-[#64748B] flex items-center justify-center">
                      <span className="text-xs text-white font-medium">+{vipWithImages.length - 12}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* CTA */}
              <div className="flex items-center gap-1 text-white/80 group-hover:text-white flex-shrink-0 transition-colors">
                <span className="text-xs sm:text-sm hidden sm:inline">{t('home.discover_all')}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </Link>
      )}

      {/* Recent Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="recent-section">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#1E293B]">{t('home.recent')}</h2>
            <div className="gold-accent-line mt-2" />
          </div>
          <Link to="/suche" className="flex items-center gap-1 text-sm text-[#475569] hover:text-[#1E293B] transition-colors" data-testid="view-all-link">
            {t('home.view_all')} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-sm border border-[#E2E8F0] overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-[#F1F5F9]" />
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-[#F1F5F9] rounded w-16" />
                  <div className="h-5 bg-[#F1F5F9] rounded w-3/4" />
                  <div className="h-3 bg-[#F1F5F9] rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : recent.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 sm:gap-6 bg-white sm:bg-transparent rounded-sm sm:rounded-none border sm:border-0 border-[#E2E8F0] overflow-hidden">
            {recent.map((item, i) => (
              <ObituaryCard key={item.id} item={item} index={i} lang={lang} />
            ))}
          </div>
        ) : (
          <p className="text-center text-[#94A3B8] py-12">{t('home.no_results')}</p>
        )}
      </section>

      {/* Anteilnahme der Gemeinschaft */}
      {(loading || recentCondolences.length > 0) && (
        <section className="bg-[#FAFAF8] border-t border-[#E8E0D0] py-16" data-testid="condolences-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-4 h-4 text-[#C5A059]" />
                  <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#1E293B]">
                    Anteilnahme der Gemeinschaft
                  </h2>
                </div>
                <div className="gold-accent-line mt-2" />
                <p className="text-sm text-[#64748B] mt-3">
                  Menschen zeigen ihre Verbundenheit – Kerzen und Worte des Gedenkens
                </p>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="bg-white rounded-sm border border-[#E2E8F0] overflow-hidden animate-pulse">
                    <div className="aspect-square bg-[#F1F5F9]" />
                    <div className="p-3 space-y-2">
                      <div className="h-3 bg-[#F1F5F9] rounded w-3/4" />
                      <div className="h-2 bg-[#F1F5F9] rounded w-full" />
                      <div className="h-2 bg-[#F1F5F9] rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {recentCondolences.map((item) => (
                  <CondolenceCard key={item.condolence_id} item={item} lang={lang} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Besondere Gedenken */}
      {(loading || featured.length > 0) && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="featured-section">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium text-[#1E293B]">{t('home.featured')}</h2>
              <div className="gold-accent-line mt-2" />
            </div>
          </div>
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-sm border border-[#E2E8F0] overflow-hidden animate-pulse">
                  <div className="aspect-[4/3] bg-[#F1F5F9]" />
                  <div className="p-5 space-y-3">
                    <div className="h-3 bg-[#F1F5F9] rounded w-16" />
                    <div className="h-5 bg-[#F1F5F9] rounded w-3/4" />
                    <div className="h-3 bg-[#F1F5F9] rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 sm:gap-6 bg-white sm:bg-transparent rounded-sm sm:rounded-none border sm:border-0 border-[#E2E8F0] overflow-hidden">
              {featured.map((item, i) => (
                <ObituaryCard key={item.id} item={item} index={i} lang={lang} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}


function CondolenceCard({ item, lang }) {
  const ins = item.inserat;
  const memorialUrl = generateMemorialUrl(ins, lang);
  const isCandle = item.is_candle_only || !item.text;
  const snippet = item.text ? item.text.slice(0, 90) + (item.text.length > 90 ? '…' : '') : null;

  return (
    <Link
      to={memorialUrl}
      className="group bg-white rounded-sm border border-[#E8E0D0] overflow-hidden hover:border-[#C5A059] hover:shadow-md transition-all duration-200 flex flex-col"
      data-testid={`condolence-card-${item.condolence_id}`}
    >
      {/* Portrait */}
      <div className="aspect-square overflow-hidden bg-[#F5F0E8] relative flex-shrink-0">
        {ins.bild_url ? (
          <img
            src={ins.bild_url}
            alt={`${ins.vorname} ${ins.nachname}`}
            loading="lazy"
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
          />
        ) : (
          <MemorialPlaceholder
            vorname={ins.vorname}
            nachname={ins.nachname}
            kantonCode={ins.kanton_code}
            size="full"
          />
        )}
        {/* Candle overlay badge */}
        {isCandle && (
          <div className="absolute bottom-2 right-2 bg-white/90 rounded-full p-1.5 shadow-sm">
            <Flame className="w-4 h-4 text-[#C5A059]" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-1">
        {/* Deceased name */}
        <p className="font-serif-accent text-[13px] leading-tight text-[#1E293B] group-hover:text-[#C5A059] transition-colors line-clamp-1">
          {ins.vorname} {ins.nachname}
        </p>
        {ins.sterbedatum && (
          <p className="text-[10px] text-[#94A3B8] mt-0.5">
            † {new Date(ins.sterbedatum).toLocaleDateString('de-CH', { day: 'numeric', month: 'numeric', year: 'numeric' })}
          </p>
        )}

        {/* Condolence message or candle */}
        <div className="mt-2 flex-1">
          {snippet ? (
            <p className="text-[11px] text-[#475569] italic leading-relaxed line-clamp-3">
              „{snippet}"
            </p>
          ) : item.tribute_image_url ? (
            <img src={item.tribute_image_url} alt="Kerze" className="w-16 h-16 object-contain opacity-90" />
          ) : (
            <span className="text-[11px] text-[#94A3B8] flex items-center gap-1">
              <Flame className="w-3 h-3 text-[#C5A059]" /> Kerze angezündet
            </span>
          )}
        </div>

        {/* Author */}
        <p className="mt-2 text-[10px] text-[#94A3B8] truncate border-t border-[#F1F5F9] pt-2">
          von {item.user_name}
        </p>
      </div>
    </Link>
  );
}
